import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useExpenseComments, useAddComment, useUpdateComment, useDeleteComment } from '@/hooks/useComments';
import { UserAvatar } from '@/components/common/UserAvatar';
import { MessageCircle, Send, Pencil, Trash2, X, Check, Loader2 } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface ExpenseCommentsProps {
  expenseId: string;
  groupId: string;
}

function formatCommentTime(dateStr: string): string {
  try {
    return formatDistanceToNowStrict(new Date(dateStr), { addSuffix: true });
  } catch {
    return '';
  }
}

export function ExpenseComments({ expenseId, groupId }: ExpenseCommentsProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: comments = [], isLoading } = useExpenseComments(expenseId);
  const addComment = useAddComment();
  const updateComment = useUpdateComment();
  const deleteComment = useDeleteComment();

  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const handleAdd = async () => {
    if (!newComment.trim() || !user) return;
    await addComment.mutateAsync({
      expenseId,
      groupId,
      userId: user.id,
      commentText: newComment.trim(),
    });
    setNewComment('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingText.trim()) return;
    await updateComment.mutateAsync({
      commentId: editingId,
      expenseId,
      commentText: editingText.trim(),
    });
    setEditingId(null);
    setEditingText('');
  };

  const handleDelete = async (commentId: string) => {
    if (!confirm(t('expenses:alert.deleteCommentConfirm'))) return;
    await deleteComment.mutateAsync({ commentId, expenseId, groupId });
  };

  return (
    <div>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-quaternary mb-3">
        <MessageCircle className="h-3.5 w-3.5" />
        {t('expenses:detail.comments')} {comments.length > 0 && `(${comments.length})`}
      </h3>

      {/* Comment list */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
        </div>
      ) : comments.length === 0 ? (
        <div className="flex flex-col items-center py-6 text-text-quaternary">
          <MessageCircle className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">{t('expenses:comment.noComments')}</p>
        </div>
      ) : (
        <div className="space-y-3 mb-4">
          {comments.map(comment => {
            const isOwn = comment.user_id === user?.id;
            const isEditing = editingId === comment.id;

            return (
              <div key={comment.id} className="group flex gap-2.5">
                <UserAvatar
                  name={comment.user?.display_name}
                  avatarUrl={comment.user?.avatar_url}
                  size="sm"
                  className="mt-0.5 shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-text-primary truncate">
                      {isOwn ? t('common:you') : comment.user?.display_name ?? t('web:common.unknownUser')}
                    </span>
                    <span className="text-xs text-text-quaternary shrink-0">
                      {formatCommentTime(comment.created_at)}
                    </span>
                    {comment.updated_at && (
                      <span className="text-xs text-text-quaternary italic">(edited)</span>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="mt-1">
                      <textarea
                        value={editingText}
                        onChange={e => setEditingText(e.target.value)}
                        className="w-full rounded-lg border border-border-primary px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none resize-none"
                        rows={2}
                        maxLength={2000}
                        autoFocus
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button
                          onClick={handleSaveEdit}
                          disabled={!editingText.trim() || updateComment.isPending}
                          className="flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                          {updateComment.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Check className="h-3 w-3" />
                          )}
                          {t('common:button.save')}
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setEditingText(''); }}
                          className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs text-text-tertiary hover:bg-surface-hover"
                        >
                          <X className="h-3 w-3" />
                          {t('common:button.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-text-secondary whitespace-pre-wrap break-words">
                      {comment.comment_text}
                    </p>
                  )}
                </div>

                {/* Action buttons - visible on hover for own comments */}
                {isOwn && !isEditing && (
                  <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={() => { setEditingId(comment.id); setEditingText(comment.comment_text); }}
                      className="rounded p-1 text-text-quaternary hover:text-text-secondary hover:bg-surface-hover"
                      title={t('common:button.edit')}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="rounded p-1 text-text-quaternary hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                      title={t('common:button.delete')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New comment input */}
      <div className="flex gap-2 items-end">
        <textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('expenses:comment.addPlaceholder')}
          maxLength={2000}
          rows={1}
          className="flex-1 rounded-lg border border-border-primary px-3 py-2 text-sm placeholder:text-text-quaternary focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none resize-none"
        />
        <button
          onClick={handleAdd}
          disabled={!newComment.trim() || addComment.isPending}
          className="flex items-center justify-center rounded-lg bg-brand-600 p-2 text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          title={t('web:expenseComments.send')}
        >
          {addComment.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}
